import { FormatOptions } from '@doss/common-all'
import { MenuItem, MenuProps, Stack, Typography } from '@mui/material'
import { useGridApiContext } from '@mui/x-data-grid-premium'
import { SyntheticEvent, useMemo, useState } from 'react'
import { FiFilter } from 'react-icons/fi'
import { RxColumns, RxDownload, RxPlus } from 'react-icons/rx'
import { tss } from 'tss-react'
import { useGetTableChildAndBacklinkTablesQuery } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { DetailedTable } from '~/components/tables/types'
import { Menu } from '~/components/ui/atoms/menu/Menu'
import { useTypedTranslation } from '../../../common/hooks/useTypedTranslation'
import { CreateTableColumnDialog } from '../../table-columns/CreateTableColumnDialog'
import { TableMenuOptions, TableMenuTypes, useTableMenuContext } from './TableMenus'

interface DossTableLevelMenuProps extends MenuProps {
  handleClose: (event: Event | SyntheticEvent) => void
  table: DetailedTable
  readOnly: boolean
}

/**
 * Table level menu for the DossTable that allows for filtering, selecting columns, and exporting.
 */
export function DossTableLevelMenu({
  handleClose,
  table,
  anchorEl,
  open,
  readOnly,
}: DossTableLevelMenuProps) {
  const { classes } = useStyles()
  const { openMenu } = useTableMenuContext()
  const { isAdmin, isInternal, permissionsManager } = useGlobalContext()
  const apiRef = useGridApiContext()
  const [openCreateTableColumnDialog, setOpenCreateTableColumnDialog] = useState(false)
  const { t } = useTypedTranslation()

  // Fetch backlinked tables for table-level export options
  const { data: tableWithBacklinkTables } = useGetTableChildAndBacklinkTablesQuery({
    variables: { tableId: table.id },
    skip: !table.id || !open,
  })

  // Find all backlinked tables with lookup column groups that have showBacklink=true pointing to
  // this table. We deduplicate by lookup column group ID since multiple columns can belong to the
  // same group (e.g., looking up Name, Email, Phone from the same relationship). Each unique
  // lookup column group represents one distinct relationship to export.
  const exportableBacklinkTables = useMemo(() => {
    const lookupBacklinkTables = tableWithBacklinkTables?.table.lookupBacklinkTables ?? []
    const lookupBacklinkTableIds = new Set(lookupBacklinkTables.map((t) => t.id))

    // Get all lookup columns from other tables that point to this table with showBacklink=true
    const showBacklinkColumns = table.columns
      .flatMap((column) => column.lookupColumnChildren)
      .filter((column) => column.lookupColumnGroup?.showBacklink === true)
      .filter((column) => lookupBacklinkTableIds.has(column.table.id))

    // Deduplicate by lookup column group ID - we only need one column per group for the export
    const seenGroupIds = new Set<string>()
    const uniqueByGroup = showBacklinkColumns.filter((column) => {
      const groupId = column.lookupColumnGroup?.id
      if (!groupId || seenGroupIds.has(groupId)) {
        return false
      }
      seenGroupIds.add(groupId)
      return true
    })

    // Create one export option per unique lookup column group
    return uniqueByGroup
      .map((column) => {
        const backlinkedTable = lookupBacklinkTables.find((t) => t.id === column.table.id)
        return {
          table: backlinkedTable,
          backlinkColumnId: column.id,
          // Use the backlink name from the lookup column group, or fall back to the column name
          backlinkName: column.lookupColumnGroup?.backlinkName ?? column.name,
        }
      })
      .filter(
        (
          item,
        ): item is {
          table: NonNullable<typeof item.table>
          backlinkColumnId: string
          backlinkName: string
        } => item.table !== undefined,
      )
  }, [tableWithBacklinkTables?.table.lookupBacklinkTables, table.columns])

  const canConfigureTableTabs = permissionsManager.canConfigureTableTabs(table.id)

  const configureTabsOptions = canConfigureTableTabs
    ? [
        <MenuItem
          key="filter"
          onClick={(e) => {
            openMenu(TableMenuTypes.COLUMN, TableMenuOptions.FILTER, undefined, apiRef)
            handleClose(e)
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <FiFilter size={12} />
            <Typography variant="body1">{t('table.filter')}</Typography>
          </Stack>
        </MenuItem>,
        <MenuItem
          key="select-columns"
          onClick={(e) => {
            openMenu(TableMenuTypes.COLUMN, TableMenuOptions.SELECT_COLUMNS, undefined, apiRef)
            handleClose(e)
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <RxColumns size={12} />
            <Typography variant="body1">{t('table.select_columns')}</Typography>
          </Stack>
        </MenuItem>,
      ]
    : []
  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 25,
          horizontal: 'right',
        }}
        className={classes.menu}
        onClose={handleClose}
        open={open}
      >
        {configureTabsOptions}
        <MenuItem
          onClick={(e) => {
            openMenu(TableMenuTypes.TABLE, TableMenuOptions.EXPORT, undefined, apiRef, table.id)
            handleClose(e)
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <RxDownload size={12} />
            <Typography variant="body1">
              {t('table.export.title', { table: table.name })}
            </Typography>
          </Stack>
        </MenuItem>
        {table.childTables.map((childTable) => (
          <MenuItem
            key={childTable.id}
            onClick={(e) => {
              openMenu(
                TableMenuTypes.TABLE,
                TableMenuOptions.EXPORT,
                undefined,
                apiRef,
                childTable.id,
              )
              handleClose(e)
            }}
          >
            <Stack
              alignItems="center"
              direction="row"
              display="flex"
              justifyContent="flex-start"
              spacing={1}
            >
              <RxDownload size={12} />
              <Typography variant="body1">
                {t('table.export.title', { table: childTable.name })}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
        {exportableBacklinkTables.map(
          ({ table: backlinkedTable, backlinkColumnId, backlinkName }) => (
            <MenuItem
              key={`${backlinkedTable.id}-${backlinkColumnId}`}
              onClick={(e) => {
                openMenu(
                  TableMenuTypes.TABLE,
                  TableMenuOptions.EXPORT,
                  undefined,
                  apiRef,
                  backlinkedTable.id,
                  backlinkColumnId,
                )
                handleClose(e)
              }}
            >
              <Stack
                alignItems="center"
                direction="row"
                display="flex"
                justifyContent="flex-start"
                spacing={1}
              >
                <RxDownload size={12} />
                <Typography variant="body1">
                  {t('table.export.title', { table: backlinkedTable.name })}
                  {backlinkName && ` (${backlinkName})`}
                </Typography>
              </Stack>
            </MenuItem>
          ),
        )}
        {!readOnly && (isAdmin || isInternal) && (
          <MenuItem
            onClick={(e) => {
              setOpenCreateTableColumnDialog(true)
              handleClose(e)
            }}
          >
            <Stack
              alignItems="center"
              direction="row"
              display="flex"
              justifyContent="flex-start"
              spacing={1}
            >
              <RxPlus className={classes.addIcon} size={12} />
              <Typography variant="body1">{t('table.create_column.title')}</Typography>
            </Stack>
          </MenuItem>
        )}
      </Menu>
      {openCreateTableColumnDialog && (
        <CreateTableColumnDialog
          columns={table.columns.map((column) => ({
            ...column,
            formatPreset: column.formatPreset ?? null,
            formatOptions: column.formatOptions as FormatOptions | null,
            formula: null,
            formulaString: null,
            shouldCreateNewLookupGroup: false,
            showLookupBacklink: false,
            lookupBacklinkName: null,
            lookupFilterExpression: null,
            lookupFilterExpressionString: null,
            // Only used to validate the new column's name against existing ones
            enumOptionInputs: null,
          }))}
          onClose={() => setOpenCreateTableColumnDialog(false)}
          parentTable={table.parentTable}
          // Modified the table so it matches the table that is expected at the Table Settings page
          table={{
            ...table,
            columns: table.columns.map((column) => ({
              ...column,
              hasNonNullCells: false,
              hasNullCells: false,
              lookupFilterExpression: null,
              lookupFilterExpressionString: null,
            })),
          }}
        />
      )}
    </>
  )
}

const useStyles = tss.create({
  link: {
    textDecoration: 'none',
  },
  addIcon: { fontSize: '12px' },
  menu: {
    zIndex: 1298,
    fontSize: '12px',
  },
})
